/**
 * Shopify → Supabase Edge → Klaviyo profile-import.
 *
 * 1) collectFormAsObject(form) — form data (checkboxes sharing one name → array).
 * 2) buildProfileImportBody(flatFields, options?) — JSON:API body for POST /api/profile-import.
 * 3) postProfileImportViaSupabase({ klaviyoBody, functionUrl?, supabaseAnonKey?, klaviyoNewsletterListId? }) — POST to the Edge function.
 *
 * Default URL: DEFAULT_SUPABASE_FUNCTION_URL.
 * Key: window.SUPABASE_ANON_KEY or supabaseAnonKey.
 * Newsletter list: window.KLAVIYO_NEWSLETTER_LIST_ID or klaviyoNewsletterListId (Klaviyo List ID) → sent in the body as klaviyoListId.
 *
 * curl ... --data '{"klaviyo":{...},"klaviyoListId":"<Newsletter_list_ID>"}'
 *
 * @example
 *   const raw = KlaviyoProfileImport.collectFormAsObject(formEl);
 *   const klaviyoBody = KlaviyoProfileImport.buildProfileImportBody(raw);
 *   const res = await KlaviyoProfileImport.postProfileImportViaSupabase({ klaviyoBody });
 */
(function (global) {
  'use strict';

  const DEFAULT_SUPABASE_FUNCTION_URL =
    'https://hjdomxzbruxedccddyna.supabase.co/functions/v1/Send-Shopify-form-to-Klaviyo';

  const PROFILE_ATTRIBUTE_KEYS = new Set([
    'first_name',
    'last_name',
    'phone_number',
    'external_id',
    'organization',
    'locale',
    'title',
    'image',
    '_kx',
  ]);

  function isNonEmptyEmail(email) {
    if (email == null || typeof email !== 'string') return false;
    const trimmed = email.trim();
    if (!trimmed) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  }

  /**
   * Collects form fields into a flat object. Multiple values sharing one name
   * (checkboxes) become an array.
   * @param {HTMLFormElement} form
   * @returns {Record<string, unknown>}
   */
  function collectFormAsObject(form) {
    if (!form || typeof form !== 'object' || form.nodeName !== 'FORM') {
      throw new Error('Klaviyo: collectFormAsObject expects an HTMLFormElement');
    }
    const fd = new FormData(form);
    /** @type {Record<string, unknown>} */
    const out = {};
    for (const [key, value] of fd.entries()) {
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        const prev = out[key];
        out[key] = Array.isArray(prev) ? [...prev, value] : [prev, value];
      } else {
        out[key] = value;
      }
    }
    return out;
  }

  /**
   * @param {Record<string, unknown>} input — flat fields; email is required.
   * @param {{ id?: string, extraReservedKeys?: string[] }} [options]
   * @returns {{ data: { type: 'profile', id?: string, attributes: Record<string, unknown> } }}
   */
  function buildProfileImportBody(input, options) {
    if (!input || typeof input !== 'object') {
      throw new Error('Klaviyo profile-import: input must be a non-null object');
    }

    const opts = options || {};
    const emailRaw = input.email;
    if (!isNonEmptyEmail(emailRaw)) {
      throw new Error('Klaviyo profile-import: valid email is required');
    }

    const email = String(emailRaw).trim();
    const reserved = new Set(PROFILE_ATTRIBUTE_KEYS);
    if (Array.isArray(opts.extraReservedKeys)) {
      for (const k of opts.extraReservedKeys) {
        if (typeof k === 'string' && k && k !== 'email' && k !== 'properties') {
          reserved.add(k);
        }
      }
    }

    /** @type {Record<string, unknown>} */
    const attributes = { email };
    /** @type {Record<string, unknown>} */
    const properties = {};

    if (input.properties != null && typeof input.properties === 'object' && !Array.isArray(input.properties)) {
      Object.assign(properties, /** @type {Record<string, unknown>} */ (input.properties));
    }

    /** Klaviyo: attributes.location (e.g. country from Shopify country select). */
    /** @type {Record<string, unknown> | null} */
    let locationAttrs = null;
    if (input.location != null && typeof input.location === 'object' && !Array.isArray(input.location)) {
      locationAttrs = { .../** @type {Record<string, unknown>} */ (input.location) };
    }
    if (input.country != null && String(input.country).trim() !== '') {
      if (!locationAttrs) locationAttrs = {};
      locationAttrs.country = String(input.country).trim();
    }
    if (locationAttrs && Object.keys(locationAttrs).length > 0) {
      attributes.location = locationAttrs;
    }

    for (const key of Object.keys(input)) {
      if (key === 'email' || key === 'properties' || key === 'country' || key === 'location') continue;
      const value = input[key];
      if (value === undefined) continue;

      if (reserved.has(key)) {
        attributes[key] = value;
        continue;
      }

      properties[key] = value;
    }

    if (Object.keys(properties).length > 0) {
      attributes.properties = properties;
    }

    /** @type {{ type: 'profile'; id?: string; attributes: Record<string, unknown> }} */
    const data = { type: 'profile', attributes };
    if (opts.id != null && String(opts.id).length > 0) {
      data.id = String(opts.id);
    }

    return { data };
  }

  /**
   * Edge function body: { klaviyo, klaviyoListId? } — the list id used for Bulk
   * Subscribe plus the Newsletter list.
   * @param {object} klaviyoBody — the result of buildProfileImportBody
   * @param {string} [klaviyoNewsletterListId] — Klaviyo List ID (the "Newsletter" list)
   */
  function wrapKlaviyoBodyForSupabase(klaviyoBody, klaviyoNewsletterListId) {
    /** @type {Record<string, unknown>} */
    const payload = { klaviyo: klaviyoBody };
    const listId =
      typeof klaviyoNewsletterListId === 'string' && klaviyoNewsletterListId.trim()
        ? klaviyoNewsletterListId.trim()
        : '';
    if (listId) {
      payload.klaviyoListId = listId;
    }
    return payload;
  }

  /**
   * @param {{ klaviyoBody: object, functionUrl?: string, supabaseAnonKey?: string, klaviyoNewsletterListId?: string }} args
   * @returns {Promise<Response>}
   */
  async function postProfileImportViaSupabase(args) {
    const a = args || {};
    const url =
      typeof a.functionUrl === 'string' && a.functionUrl.trim() ? a.functionUrl.trim() : DEFAULT_SUPABASE_FUNCTION_URL;
    const keyFromWindow = global && typeof global.SUPABASE_ANON_KEY === 'string' ? global.SUPABASE_ANON_KEY : '';
    const key =
      typeof a.supabaseAnonKey === 'string' && a.supabaseAnonKey.trim()
        ? a.supabaseAnonKey.trim()
        : keyFromWindow.trim();
    const listFromArgs =
      typeof a.klaviyoNewsletterListId === 'string' && a.klaviyoNewsletterListId.trim()
        ? a.klaviyoNewsletterListId.trim()
        : '';
    const listFromWindow =
      global && typeof global.KLAVIYO_NEWSLETTER_LIST_ID === 'string' ? global.KLAVIYO_NEWSLETTER_LIST_ID.trim() : '';
    const newsletterListId = listFromArgs || listFromWindow;
    const klaviyoBody = a.klaviyoBody;
    if (!key) {
      throw new Error('Klaviyo: set window.SUPABASE_ANON_KEY or pass supabaseAnonKey');
    }
    if (!klaviyoBody || typeof klaviyoBody !== 'object') {
      throw new Error('Klaviyo: klaviyoBody is required');
    }

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${key}`,
        apikey: key,
      },
      body: JSON.stringify(wrapKlaviyoBodyForSupabase(klaviyoBody, newsletterListId)),
    });
  }

  const api = {
    DEFAULT_SUPABASE_FUNCTION_URL,
    isNonEmptyEmail,
    collectFormAsObject,
    buildProfileImportBody,
    wrapKlaviyoBodyForSupabase,
    postProfileImportViaSupabase,
  };

  global.KlaviyoProfileImport = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : window);

/*
 * --- Supabase Edge Function: supabase/functions/klaviyo-profile-upsert/index.ts ---
 * 1) POST /api/profile-import — upsert the profile from { klaviyo } sent by Shopify.
 * 2) On success (200/201) — Bulk Subscribe with historical_import + consented_at
 *    (no double opt-in email).
 *
 *   supabase secrets set KLAVIYO_PRIVATE_KEY=pk_...
 *   # or: supabase secrets set KLAVIYO_API_KEY=pk_...
 *   # The "Newsletter" list ID from Klaviyo (Lists → Newsletter) — either from the
 *   # theme, or as a fallback:
 *   supabase secrets set KLAVIYO_LIST_ID=XXXXXXXX
 *   supabase functions deploy klaviyo-profile-upsert
 *
 * The key needs these scopes: profiles:write, subscriptions:write, lists:write
 * (for subscribe + list).
 * Docs: https://developers.klaviyo.com/en/reference/bulk_subscribe_profiles
 *
 * Klaviyo's rule for consented_at (from the docs):
 * — The field is only for historical consent import; use it together with
 *   historical_import: true.
 * — If historical_import is NOT sent, any consented_at value → error.
 * So either (historical_import + consented_at), or (neither: consent SUBSCRIBED
 * only, with the list's double opt-in turned off).
 *
 * ```ts
 * import "jsr:@supabase/functions-js/edge-runtime.d.ts";
 *
 * const KLAVIYO_PROFILE_IMPORT_URL = "https://a.klaviyo.com/api/profile-import";
 * const KLAVIYO_SUBSCRIBE_URL = "https://a.klaviyo.com/api/profile-subscription-bulk-create-jobs";
 * const KLAVIYO_REVISION = "2026-01-15";
 *
 * function corsHeaders(req: Request): Record<string, string> {
 *   const origin = req.headers.get("Origin") ?? "*";
 *   return {
 *     "Access-Control-Allow-Origin": origin,
 *     "Access-Control-Allow-Methods": "POST, OPTIONS",
 *     "Access-Control-Allow-Headers": "authorization, x-client-info, apikey, content-type",
 *   };
 * }
 *
 * function jsonError(req: Request, status: number, message: string): Response {
 *   return new Response(JSON.stringify({ error: message }), {
 *     status,
 *     headers: { ...corsHeaders(req), "Content-Type": "application/json" },
 *   });
 * }
 *
 * function klaviyoHeaders(apiKey: string): Record<string, string> {
 *   return {
 *     Authorization: `Klaviyo-API-Key ${apiKey}`,
 *     "Content-Type": "application/vnd.api+json",
 *     Accept: "application/vnd.api+json",
 *     revision: KLAVIYO_REVISION,
 *   };
 * }
 *
 * Deno.serve(async (req) => {
 *   if (req.method === "OPTIONS") {
 *     return new Response("ok", { headers: corsHeaders(req) });
 *   }
 *   if (req.method !== "POST") {
 *     return new Response("Method Not Allowed", { status: 405, headers: corsHeaders(req) });
 *   }
 *
 *   let wrapper: { klaviyo?: unknown; klaviyoListId?: string };
 *   try {
 *     wrapper = await req.json();
 *   } catch {
 *     return jsonError(req, 400, "Invalid JSON");
 *   }
 *
 *   const listId =
 *     (typeof wrapper.klaviyoListId === "string" ? wrapper.klaviyoListId : "").trim() ||
 *     (Deno.env.get("KLAVIYO_LIST_ID") ?? "").trim();
 *   if (!listId) {
 *     return jsonError(
 *       req,
 *       400,
 *       "klaviyoListId in JSON or KLAVIYO_LIST_ID secret required (Klaviyo Newsletter list ID)",
 *     );
 *   }
 *
 *   const klaviyoBody = wrapper?.klaviyo;
 *   if (!klaviyoBody || typeof klaviyoBody !== "object") {
 *     return jsonError(req, 400, "Missing klaviyo wrapper");
 *   }
 *
 *   const data = (klaviyoBody as { data?: unknown }).data;
 *   if (!data || typeof data !== "object") {
 *     return jsonError(req, 400, "Missing data");
 *   }
 *
 *   const record = data as {
 *     type?: string;
 *     attributes?: { email?: string; properties?: Record<string, unknown> };
 *   };
 *   if (record.type !== "profile") {
 *     return jsonError(req, 400, "data.type must be profile");
 *   }
 *   const email = record.attributes?.email;
 *   if (typeof email !== "string" || !email.trim()) {
 *     return jsonError(req, 400, "attributes.email is required");
 *   }
 *
 *   const apiKey =
 *     (Deno.env.get("KLAVIYO_PRIVATE_KEY") ?? Deno.env.get("KLAVIYO_API_KEY") ?? "").trim();
 *   if (!apiKey) {
 *     return jsonError(req, 500, "Klaviyo API key not configured on server");
 *   }
 *
 *   const profileRes = await fetch(KLAVIYO_PROFILE_IMPORT_URL, {
 *     method: "POST",
 *     headers: klaviyoHeaders(apiKey),
 *     body: JSON.stringify(klaviyoBody),
 *   });
 *
 *   const profileText = await profileRes.text();
 *   if (!profileRes.ok) {
 *     return new Response(profileText, {
 *       status: profileRes.status,
 *       headers: {
 *         ...corsHeaders(req),
 *         "Content-Type": profileRes.headers.get("Content-Type") ?? "application/json",
 *       },
 *     });
 *   }
 *
 *   // historical_import + consented_at go together per the docs; never send
 *   // consented_at without the flag.
 *   // consented_at must be a valid date-time in the past (see the API's timestamp error).
 *   // Take properties.submittedAt from the form body; offset it at least 5 min
 *   // from now; ISO without milliseconds.
 *   const toKlaviyoConsentedAt = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, "Z");
 *   const minPast = Date.now() - 300_000;
 *   const rawSubmitted = record.attributes?.properties?.submittedAt;
 *   let consentedMs: number;
 *   if (typeof rawSubmitted === "string" && rawSubmitted.trim()) {
 *     const parsed = Date.parse(rawSubmitted.trim());
 *     consentedMs = Number.isFinite(parsed) ? Math.min(parsed, minPast) : minPast;
 *   } else {
 *     consentedMs = minPast;
 *   }
 *   const consentedAt = toKlaviyoConsentedAt(new Date(consentedMs));
 *
 *   const jobData: Record<string, unknown> = {
 *     type: "profile-subscription-bulk-create-job",
 *     attributes: {
 *       historical_import: true,
 *       custom_source: "shopify_htc_lead_gate_newsletter",
 *       profiles: {
 *         data: [
 *           {
 *             type: "profile",
 *             attributes: {
 *               email: email.trim(),
 *               subscriptions: {
 *                 email: {
 *                   marketing: {
 *                     consent: "SUBSCRIBED",
 *                     consented_at: consentedAt,
 *                   },
 *                 },
 *               },
 *             },
 *           },
 *         ],
 *       },
 *     },
 *     relationships: {
 *       list: { data: { type: "list", id: listId } },
 *     },
 *   };
 *
 *   // Alternative (per the docs): drop historical_import AND omit consented_at —
 *   // send only marketing: { consent: "SUBSCRIBED" }.
 *   // Turn off double opt-in for the Newsletter list in Klaviyo, otherwise a
 *   // confirmation email goes out.
 *
 *   const subscribeRes = await fetch(KLAVIYO_SUBSCRIBE_URL, {
 *     method: "POST",
 *     headers: klaviyoHeaders(apiKey),
 *     body: JSON.stringify({ data: jobData }),
 *   });
 *
 *   const subscribeText = await subscribeRes.text();
 *   if (!subscribeRes.ok) {
 *     return new Response(
 *       JSON.stringify({
 *         error: "Profile saved but subscribe failed",
 *         profile: { status: profileRes.status, body: profileText },
 *         subscribe: { status: subscribeRes.status, body: subscribeText },
 *       }),
 *       {
 *         status: 502,
 *         headers: { ...corsHeaders(req), "Content-Type": "application/json" },
 *       },
 *     );
 *   }
 *
 *   return new Response(
 *     JSON.stringify({
 *       profile: { status: profileRes.status, body: profileText },
 *       subscribe: { status: subscribeRes.status, body: subscribeText },
 *     }),
 *     { status: 200, headers: { ...corsHeaders(req), "Content-Type": "application/json" } },
 *   );
 * });
 * ```
 *
 * Note: the theme checks res.ok — 200 is fine.
 * In Klaviyo, historical_import also bypasses the "Added to list" flow for that
 * job — trigger the welcome email another way if you need it.
 * Double opt-in can be turned off in the Newsletter list settings;
 * historical_import should only be used with real consent given via the form.
 */
